// Scout backend client. Mirrors the auth pattern in applicationApi.js.

import { localeRequestHeaders } from './localePreference';
import { apiErrorMessage } from './apiErrorMessages';

const API_PREFIX = '/api/v1';

export function browseScoutJobs({ user, signal, ...filters }) {
  const values = Object.fromEntries(Object.entries(filters).filter(([, value]) => value !== '' && value != null));
  return apiRequest(`/opportunities/browse?${new URLSearchParams(values)}`, user, { signal });
}

export function getScoutInventoryJob({ user, jobId, signal }) {
  return apiRequest(`/opportunities/browse/${encodeURIComponent(jobId)}`, user, { signal });
}

async function apiRequest(path, user, options = {}) {
  const token = await user.getIdToken();
  let response;

  try {
    response = await fetch(`${API_PREFIX}${path}`, {
      ...options,
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...localeRequestHeaders(),
        ...(options.headers || {}),
      },
    });
  } catch {
    throw new Error(apiErrorMessage('scoutNetwork'));
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    const requestError = new Error(error.detail || apiErrorMessage('scoutRequest'));
    requestError.status = response.status;
    throw requestError;
  }

  return response.json();
}

// Persists the onboarding-produced scout document and kicks off backend
// processing (cluster upsert in BigQuery + Cloud Tasks embed job). The
// backend writes both the profile delta and the nested scout document to
// RTDB itself, so the caller does NOT need a separate update() call.
export function finalizeScout({ user, scoutDocument, profileUpdate }) {
  return apiRequest('/scout/onboarding/finalize', user, {
    method: 'POST',
    body: JSON.stringify({ scoutDocument, profileUpdate }),
  });
}

// Edit an existing Scout from the dedicated Edit Job Scout screen. The server
// owns rebuilding the semantic profile, reclustering, replacing old GCP rows,
// and launching the same immediate processing path used after onboarding.
export function updateScoutConfiguration({ user, scoutId, ...configuration }) {
  return apiRequest(`/scout/${scoutId}/update`, user, {
    method: 'POST',
    body: JSON.stringify(configuration),
  });
}

export function postScoutFeedback({ user, scoutId, jobId, action, reason }) {
  return apiRequest(`/scout/${scoutId}/feedback`, user, {
    method: 'POST',
    body: JSON.stringify({ jobId, action, reason }),
  });
}

export function refreshScout({ user, scoutId }) {
  return apiRequest(`/scout/${scoutId}/refresh`, user, {
    method: 'POST',
  });
}

// Rebuild the existing Scout from the user's newly uploaded CV, then refresh
// its matches without making the user repeat Scout setup.
export function regenerateScoutFromCv({ user, scoutId }) {
  return apiRequest(`/scout/${scoutId}/regenerate`, user, {
    method: 'POST',
  });
}

export function refineScoutDetails({ user, scoutId, targetStates, workAuthorizationCountries, professionalLanguages, salaryPreference, dealbreakers }) {
  return apiRequest(`/scout/${scoutId}/refine`, user, {
    method: 'POST',
    body: JSON.stringify({ targetStates, workAuthorizationCountries, professionalLanguages, salaryPreference, dealbreakers }),
  });
}
